import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, Box, MenuItem, TextField, Alert, CircularProgress,
  Paper
} from '@mui/material';
import { MergeType as MergeIcon } from '@mui/icons-material';
import moment from 'moment';

const MergeLeadsDialog = ({ open, onClose, customerGroup, onMerge }) => {
  const [primaryLeadId, setPrimaryLeadId] = useState('');
  const [secondaryLeadId, setSecondaryLeadId] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open && customerGroup?.leads?.length >= 2) {
      setPrimaryLeadId(customerGroup.leads[0]._id);
      setSecondaryLeadId(customerGroup.leads[1]._id);
      setLoading(false);
    }
  }, [open, customerGroup]);

  if (!customerGroup || customerGroup.leads.length < 2) return null;

  const handleConfirm = async () => {
    if (!primaryLeadId || !secondaryLeadId || primaryLeadId === secondaryLeadId) return;
    setLoading(true);
    try {
      const primaryLead = customerGroup.leads.find(l => l._id === primaryLeadId);
      const secondaryLead = customerGroup.leads.find(l => l._id === secondaryLeadId);
      await onMerge(primaryLead, secondaryLead);
      onClose();
    } catch (err) {
      console.error('Failed to merge leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const primaryLead = customerGroup.leads.find(l => l._id === primaryLeadId);
  const secondaryLead = customerGroup.leads.find(l => l._id === secondaryLeadId);

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box sx={{
          p: 1, borderRadius: 2, bgcolor: '#fef3c7', color: '#b45309',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <MergeIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Merge Duplicate Inquiries
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Combine duplicate trip entries for <strong>{customerGroup.guest_name}</strong>
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
          Merging consolidates notes, call history, and remarks into the <strong>Primary Inquiry</strong> and deletes the redundant duplicate.
        </Alert>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
          {/* Primary Lead Picker */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f0fdf4', borderColor: '#86efac' }}>
            <Typography variant="subtitle2" fontWeight={700} color="#15803d" sx={{ mb: 1 }}>
              1. Keep as Primary Inquiry:
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={primaryLeadId}
              onChange={(e) => {
                const val = e.target.value;
                setPrimaryLeadId(val);
                if (val === secondaryLeadId) {
                  const alt = customerGroup.leads.find(l => l._id !== val);
                  if (alt) setSecondaryLeadId(alt._id);
                }
              }}
            >
              {customerGroup.leads.map(lead => (
                <MenuItem key={lead._id} value={lead._id} disabled={lead._id === secondaryLeadId}>
                  {lead.destination || 'N/A'} ({lead.pax || 0} Pax) — {moment(lead.createdAt).format('DD MMM')}
                </MenuItem>
              ))}
            </TextField>

            {primaryLead && (
              <Box sx={{ mt: 1.5, fontSize: '0.8rem', color: '#166534' }}>
                <Typography variant="caption" display="block"><strong>Status:</strong> {primaryLead.status}</Typography>
                <Typography variant="caption" display="block"><strong>Date:</strong> {moment(primaryLead.createdAt).format('DD MMM YYYY')}</Typography>
                <Typography variant="caption" display="block"><strong>Calls Logged:</strong> {primaryLead.call_history?.length || 0}</Typography>
                <Typography variant="caption" display="block"><strong>Quotations:</strong> {primaryLead.quotationsCount || 0}</Typography>
              </Box>
            )}
          </Paper>

          {/* Duplicate Lead Picker */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fff1f2', borderColor: '#fca5a5' }}>
            <Typography variant="subtitle2" fontWeight={700} color="#be123c" sx={{ mb: 1 }}>
              2. Duplicate to Merge & Remove:
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={secondaryLeadId}
              onChange={(e) => {
                const val = e.target.value;
                setSecondaryLeadId(val);
                if (val === primaryLeadId) {
                  const alt = customerGroup.leads.find(l => l._id !== val);
                  if (alt) setPrimaryLeadId(alt._id);
                }
              }}
            >
              {customerGroup.leads.map(lead => (
                <MenuItem key={lead._id} value={lead._id} disabled={lead._id === primaryLeadId}>
                  {lead.destination || 'N/A'} ({lead.pax || 0} Pax) — {moment(lead.createdAt).format('DD MMM')}
                </MenuItem>
              ))}
            </TextField>

            {secondaryLead && (
              <Box sx={{ mt: 1.5, fontSize: '0.8rem', color: '#9f1239' }}>
                <Typography variant="caption" display="block"><strong>Status:</strong> {secondaryLead.status}</Typography>
                <Typography variant="caption" display="block"><strong>Date:</strong> {moment(secondaryLead.createdAt).format('DD MMM YYYY')}</Typography>
                <Typography variant="caption" display="block"><strong>Calls to Transfer:</strong> {secondaryLead.call_history?.length || 0}</Typography>
                <Typography variant="caption" display="block"><strong>Will be deleted:</strong> Yes (merged)</Typography>
              </Box>
            )}
          </Paper>
        </Box>

        <Typography variant="body2" color="text.secondary">
          ✓ All call logs from the secondary lead will be appended to the primary lead.<br />
          ✓ Any remarks from the secondary lead will be appended.<br />
          ✓ The secondary duplicate record will be removed from the database.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!primaryLeadId || !secondaryLeadId || primaryLeadId === secondaryLeadId || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <MergeIcon />}
          sx={{
            background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
            fontWeight: 600,
            px: 3
          }}
        >
          {loading ? 'Merging...' : 'Confirm & Merge Inquiries'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeLeadsDialog;
